import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

/** Normalized CRM contact row from pm-api (camelCase or legacy PascalCase). */
export interface CrmContactApi {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  companyType?: string | null;
  companyReg?: string | null;
  eMail?: string | null;
  phone?: string | null;
  source?: string | null;
  status?: number | null;
  isArchive?: boolean | null;
  dateOfCreation?: string | null;
  lastUpdatedOn?: string | null;
  empCount?: number | null;
  turnOver?: number | null;
}

export interface CrmContactListResponseApi {
  items: CrmContactApi[];
  totalCount: number;
}

export interface CrmContactUpsertPayloadApi {
  id: number;
  parentUserId: number;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  companyType?: string | null;
  companyReg?: string | null;
  eMail?: string | null;
  phone?: string | null;
  source?: string | null;
  status?: number | null;
  userMasterId?: number | null;
  isArchive?: boolean | null;
  isProposalSent?: boolean | null;
  empCount?: number | null;
  turnOver?: number | null;
  contactOwner?: number | null;
  updatedBy?: number | null;
}

export interface CrmBulkStatusPayloadApi {
  parentUserId: number;
  actingUserMasterId: number;
  status: number; // 1..4
  contactIds: number[];
  /** When status is 3 (Client), create clients in Client Store (no Capium3 CompanyMaster). */
  convertToClient?: boolean;
}

export interface CrmBulkStatusResponseApi {
  requestedCount: number;
  updatedCount: number;
  conversions?: CrmContactConversionResultApi[];
}

export interface CrmContactConversionResultApi {
  contactId: number;
  success: boolean;
  clientUniversalId?: string | null;
  alreadyLinked?: boolean;
  message?: string | null;
}

export interface CrmConvertToClientPayloadApi {
  parentUserId: number;
  actingUserMasterId: number;
  contactId: number;
  updateStatusToActive?: boolean;
}

export interface CrmConvertToClientResponseApi {
  contactId: number;
  clientUniversalId: string;
  alreadyLinked: boolean;
  statusUpdated: boolean;
}

export interface CrmContactServiceItemPayloadApi {
  serviceTitle: string;
  catalogKey?: string | null;
  frequency?: number | null;
  cost?: number | null;
}

export interface CrmContactsBulkImportFailureApi {
  index: number;
  email?: string | null;
  message: string;
}

export interface CrmContactsBulkImportResponseApi {
  totalCount: number;
  importedCount: number;
  failedCount: number;
  failures: CrmContactsBulkImportFailureApi[];
}

export interface SaveCrmContactServicesPayloadApi {
  parentUserId: number;
  updatedBy?: number | null;
  services: CrmContactServiceItemPayloadApi[];
}

export interface CrmCommunicationPreferencesApi {
  parentUserId: number;
  skipDuplicateEmail: boolean;
  applyCcToAllEmails: boolean;
}

export interface CrmSourceLabelApi {
  id: number;
  label: string;
}

export interface SaveCrmCommunicationPreferencesPayloadApi {
  skipDuplicateEmail: boolean;
  applyCcToAllEmails: boolean;
  updatedBy?: number | null;
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v as string | number).trim() !== '') {
      return String(v as string | number).trim();
    }
  }
  return null;
}

function pickNum(o: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (v != null && v !== '' && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
}

function pickBool(o: Record<string, unknown>, ...keys: string[]): boolean | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    const s = String(v as string | number).trim().toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
  }
  return null;
}

/** Maps pm-api / legacy PascalCase list payloads into a stable shape for the UI. */
export function coerceCrmContact(raw: unknown): CrmContactApi {
  const o = asRecord(raw);
  return {
    id: pickNum(o, 'id', 'Id') ?? 0,
    firstName: pickStr(o, 'firstName', 'FirstName'),
    lastName: pickStr(o, 'lastName', 'LastName'),
    companyName: pickStr(o, 'companyName', 'CompanyName'),
    companyType: pickStr(o, 'companyType', 'CompanyType'),
    companyReg: pickStr(o, 'companyReg', 'CompanyReg'),
    eMail: pickStr(o, 'eMail', 'email', 'EMail'),
    phone: pickStr(o, 'phone', 'Phone', 'mobile', 'Mobile', 'mobileNo', 'MobileNo'),
    source: pickStr(o, 'source', 'Source'),
    status: pickNum(o, 'status', 'Status'),
    isArchive: pickBool(o, 'isArchive', 'IsArchive'),
    dateOfCreation: pickStr(o, 'dateOfCreation', 'DateOfCreation'),
    lastUpdatedOn: pickStr(o, 'lastUpdatedOn', 'LastUpdatedOn'),
    empCount: pickNum(o, 'empCount', 'EmpCount'),
    turnOver: pickNum(o, 'turnOver', 'TurnOver')
  };
}

export function coerceCrmContactList(res: unknown): CrmContactListResponseApi {
  const r = asRecord(res);
  const rawItems = r['items'] ?? r['Items'];
  const items = Array.isArray(rawItems) ? rawItems.map(coerceCrmContact) : [];
  const totalCount = pickNum(r, 'totalCount', 'TotalCount') ?? items.length;
  return { items, totalCount };
}

@Injectable({ providedIn: 'root' })
export class PmCrmApiService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return environment.pmApiBaseUrl?.replace(/\/$/, '') ?? '';
  }

  listContacts(req: {
    parentUserId: number;
    status?: number;
    companyType?: string | null;
    quicksearch?: string | null;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<CrmContactListResponseApi> {
    let params = new HttpParams().set('parentUserId', String(req.parentUserId));
    if (req.status != null) params = params.set('status', String(req.status));
    if (req.companyType != null) params = params.set('companyType', String(req.companyType));
    if (req.quicksearch != null) params = params.set('quicksearch', String(req.quicksearch));
    if (req.pageNumber != null) params = params.set('pageNumber', String(req.pageNumber));
    if (req.pageSize != null) params = params.set('pageSize', String(req.pageSize));
    return this.http
      .get<unknown>(`${this.base}/api/v1/crm/contacts`, { params })
      .pipe(map(coerceCrmContactList));
  }

  upsertContact(body: CrmContactUpsertPayloadApi): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/api/v1/crm/contacts`, body);
  }

  importContacts(
    parentUserId: number,
    actingUserMasterId: number,
    file: File
  ): Observable<CrmContactsBulkImportResponseApi> {
    const params = new HttpParams()
      .set('parentUserId', String(parentUserId))
      .set('actingUserMasterId', String(actingUserMasterId));
    const form = new FormData();
    form.append('file', file, file.name || 'crm-contacts-import.csv');
    return this.http.post<CrmContactsBulkImportResponseApi>(
      `${this.base}/api/v1/crm/contacts/import`,
      form,
      { params }
    );
  }

  bulkUpdateStatus(body: CrmBulkStatusPayloadApi): Observable<CrmBulkStatusResponseApi> {
    return this.http.post<CrmBulkStatusResponseApi>(`${this.base}/api/v1/crm/contacts/bulk-status`, body);
  }

  saveContactServices(contactId: number, body: SaveCrmContactServicesPayloadApi): Observable<void> {
    return this.http.put<void>(`${this.base}/api/v1/crm/contacts/${contactId}/services`, body);
  }

  convertToClient(
    contactId: number,
    body: Omit<CrmConvertToClientPayloadApi, 'contactId'>
  ): Observable<CrmConvertToClientResponseApi> {
    return this.http.post<CrmConvertToClientResponseApi>(
      `${this.base}/api/v1/crm/contacts/${contactId}/convert-to-client`,
      { ...body, contactId }
    );
  }

  getCommunicationPreferences(parentUserId: number): Observable<CrmCommunicationPreferencesApi> {
    const params = new HttpParams().set('parentUserId', String(parentUserId));
    return this.http.get<CrmCommunicationPreferencesApi>(`${this.base}/api/v1/crm/communication-preferences`, {
      params
    });
  }

  saveCommunicationPreferences(
    parentUserId: number,
    body: SaveCrmCommunicationPreferencesPayloadApi
  ): Observable<void> {
    const params = new HttpParams().set('parentUserId', String(parentUserId));
    return this.http.put<void>(`${this.base}/api/v1/crm/communication-preferences`, body, { params });
  }

  listCrmSourceLabels(parentUserId: number): Observable<CrmSourceLabelApi[]> {
    const params = new HttpParams().set('parentUserId', String(parentUserId));
    return this.http.get<CrmSourceLabelApi[]>(`${this.base}/api/v1/crm/source-labels`, { params });
  }

  saveCrmSourceLabel(parentUserId: number, label: string): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/api/v1/crm/source-labels`, { parentUserId, label });
  }

  listContactServices(contactId: number, parentUserId: number): Observable<CrmContactServiceApi[]> {
    const params = new HttpParams().set('parentUserId', String(parentUserId));
    return this.http
      .get<{ items: CrmContactServiceApi[] }>(`${this.base}/api/v1/crm/contacts/${contactId}/services`, { params })
      .pipe(map((res) => res.items ?? []));
  }

  getClientLink(contactId: number, parentUserId: number): Observable<CrmContactClientLinkApi> {
    const params = new HttpParams().set('parentUserId', String(parentUserId));
    return this.http.get<CrmContactClientLinkApi>(`${this.base}/api/v1/crm/contacts/${contactId}/client-link`, { params });
  }
}

export interface CrmContactServiceApi {
  id: number;
  serviceTitle: string | null;
  frequency: number | null;
  cost: number | null;
  dateOfCreation: string | null;
}

export interface CrmContactClientLinkApi {
  isLinked: boolean;
  clientUniversalId: string | null;
  companyMasterId: number | null;
  clientName: string | null;
}
